import { AbortableAPI } from 'utils';
import { GW_SERVICES_PREFIXES } from 'constants/gateway';

import type {
  AttachmentResponseDTO,
  ChatAttachmentsPageDTO,
  ChatEmployeeDTO,
  ChatsResponseDTO,
  ChatStatsDTO,
  ForwardResponseDTO,
  HistoryResponseDTO,
  MapResponseDTO,
  MapsResponseDTO,
  MassiveMessagesPageDTO,
  MessageEditsResponseDTO,
  MessageSearchPageDTO,
  ParticipantDTO,
  SuccessResultDTO,
  SystemEntitiesAvailabilityDTO,
} from './dto';
import type {
  ChangeMessageParams,
  DeleteMessageParams,
  DisconnectMapParams,
  ForwardMessageParams,
  GetAttachmentParams,
  GetChatAttachmentsParams,
  GetChatsParams,
  GetChatStatsParams,
  GetEditsParams,
  GetHistoryParams,
  GetMapsParams,
  GetMassiveMessagesParams,
  MapChatParams,
  MarkReadParams,
  SearchMessagesParams,
  SystemEntitiesAvailabilityParams,
} from './types';

const { REQUEST } = GW_SERVICES_PREFIXES;
const PREFIX = `${REQUEST}/max_chat` as const;

export const GetChats = (
  params: GetChatsParams,
  signal?: AbortSignal
): Promise<ChatsResponseDTO> =>
  AbortableAPI.get(`/${PREFIX}/chats`, {
    params,
    signal,
  });

export const SearchMessages = (
  params: SearchMessagesParams,
  signal?: AbortSignal
): Promise<MessageSearchPageDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/search`, { params, signal });

export const GetChatStats = (
  params: GetChatStatsParams,
  signal?: AbortSignal
): Promise<ChatStatsDTO> =>
  AbortableAPI.get(`/${PREFIX}/chat_stats`, { params, signal });

export const GetMaps = (body: GetMapsParams): Promise<MapsResponseDTO> =>
  AbortableAPI.put(`/${PREFIX}/map`, { body });

export const MapChat = ({
  house_id,
  max_chat_url,
}: MapChatParams): Promise<MapResponseDTO> =>
  AbortableAPI.post(`/c300/house_max_messenger/house/${house_id}/`, {
    body: { max_chat_url },
  });

export const DeleteChatLink = (house_id: string): Promise<SuccessResultDTO> =>
  AbortableAPI.delete(`/c300/house_max_messenger/house/${house_id}/`);

export const DisconnectMap = (
  body: DisconnectMapParams
): Promise<MapResponseDTO> =>
  AbortableAPI.patch(`/${PREFIX}/map/disconnect`, { body });

export const MapByLink = (max_chat_url: string): Promise<MapResponseDTO[]> =>
  AbortableAPI.get(`/${PREFIX}/map/by_link`, {
    params: { max_chat_url },
  });

export const GetHistory = (
  params: GetHistoryParams,
  signal?: AbortSignal
): Promise<HistoryResponseDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/history`, { params, signal });

export const GetMassiveMessages = (
  params: GetMassiveMessagesParams,
  signal?: AbortSignal
): Promise<MassiveMessagesPageDTO> =>
  AbortableAPI.get(`/${PREFIX}/massive_messages`, { params, signal });

export const GetParticipants = (
  chat_id: number,
  signal?: AbortSignal
): Promise<ParticipantDTO[]> =>
  AbortableAPI.get(`/${PREFIX}/messages/participants`, {
    params: { chat_id },
    signal,
  });

export const GetChatEmployees = (
  chat_id: number,
  signal?: AbortSignal
): Promise<ChatEmployeeDTO[]> =>
  AbortableAPI.get(`/${PREFIX}/chat_employees`, {
    params: { chat_id },
    signal,
  });

export const GetEdits = (
  params: GetEditsParams
): Promise<MessageEditsResponseDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/get_edits`, { params });

export const GetAttachment = (
  params: GetAttachmentParams,
  signal?: AbortSignal
): Promise<AttachmentResponseDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/get_attachment`, {
    params,
    signal,
  });

export const ForwardMessage = (
  body: ForwardMessageParams
): Promise<ForwardResponseDTO> =>
  AbortableAPI.post(`/${PREFIX}/messages/forward`, { body });

export const ChangeMessage = (
  body: ChangeMessageParams
): Promise<SuccessResultDTO> =>
  AbortableAPI.put(`/${PREFIX}/messages/change`, { body });

export const DeleteMessage = (
  body: DeleteMessageParams
): Promise<SuccessResultDTO> =>
  AbortableAPI.delete(`/${PREFIX}/messages/delete`, { body });

export const MarkRead = (params: MarkReadParams): Promise<SuccessResultDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/mark_read`, { params });

export const GetChatAttachments = (
  params: GetChatAttachmentsParams,
  signal?: AbortSignal
): Promise<ChatAttachmentsPageDTO> =>
  AbortableAPI.get(`/${PREFIX}/messages/get_attachments`, {
    params,
    signal,
  });

// Какие заявки/обращения из системных сообщений сотрудник может открыть:
// не вернувшийся id удалён или вне его области видимости.
// GET, а не POST - на бэкенде это право "Чтение", а не "Создание" (шлюз
// выводит требуемое право из HTTP-метода); id одной строкой через запятую,
// а не повторяющимся query-параметром, чтобы не плодить в URL до сотен
// одноимённых параметров.
export const GetSystemEntitiesAvailability = (
  { request_ids, appeal_ids }: SystemEntitiesAvailabilityParams,
  signal?: AbortSignal
): Promise<SystemEntitiesAvailabilityDTO> =>
  AbortableAPI.get(`/${PREFIX}/system_entities/availability`, {
    params: {
      request_ids: request_ids.join(','),
      appeal_ids: appeal_ids.join(','),
    },
    signal,
  });
